import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import db from "../Firebase";

const PERIOD_START = new Date("2026-01-01T00:00:00"); // always Jan 1 for pre-existing checks

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const generateMonths = () => {
    const now    = new Date();
    const months = [];
    let year  = PERIOD_START.getFullYear();
    let month = PERIOD_START.getMonth(); // 0-indexed
    while (year < now.getFullYear() || (year === now.getFullYear() && month <= now.getMonth())) {
        months.push({
            label: `${MONTH_NAMES[month]}'${String(year).slice(2)}`,
            start: new Date(year, month, 1, 0, 0, 0),
            end:   new Date(year, month + 1, 0, 23, 59, 59),
        });
        month++;
        if (month > 11) { month = 0; year++; }
    }
    return months;
};

const MONTHS = generateMonths();

const toDate = (val) => {
    if (!val) return null;
    if (val?.toDate) return val.toDate();
    if (val instanceof Date) return val;
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const inRange = (val, start, end) => {
    const d = toDate(val);
    return d && d >= start && d <= end;
};

const inMonths = (val, selIdxs) =>
    selIdxs.length > 0 && selIdxs.some(i => inRange(val, MONTHS[i].start, MONTHS[i].end));

const fmtVal = (val) => {
    if (val === null || val === undefined || val === "") return "—";
    if (val?.toDate) return val.toDate().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    if (val instanceof Date) return val.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    return String(val);
};

const METRIC_DEFS = [
    { key: "downloads",    label: "Downloads",     icon: "📲", color: "#4361ee" },
    { key: "active",       label: "Active Users",  icon: "👥", color: "#7209b7" },
    { key: "workshops",    label: "Workshops",     icon: "🎓", color: "#f72585" },
    { key: "hours",        label: "Train. Hours",  icon: "⏱️", color: "#4cc9f0", suffix: " hrs" },
    { key: "participants", label: "Participants",  icon: "🏅", color: "#3a0ca3" },
    { key: "repeat",       label: "Repeat Part.",  icon: "🔁", color: "#480ca8" },
    { key: "portfolios",   label: "Portfolios",    icon: "🖼️", color: "#560bad" },
    { key: "collaterals",  label: "Collaterals",   icon: "📢", color: "#f3722c" },
    { key: "cities",       label: "New Cities",    icon: "🏙️", color: "#4895ef" },
    { key: "states",       label: "New States",    icon: "🗺️", color: "#3f37c9" },
];

const PROFILE_COLORS = ["#f72585", "#7209b7", "#3a0ca3", "#4895ef", "#0d9488", "#f3722c", "#4361ee", "#0891b2"];

const TAG_TO_PROFILE = { beauty: "Parlor Owner", fashion: "Boutique" };
const getProfileForTag = (tag) => TAG_TO_PROFILE[(tag || "").toLowerCase().trim()] || "Others";

const normalizeActivityId = (id) => {
    const s = String(id || "").trim();
    return s.startsWith("+91") ? s.slice(3) : s;
};
const looksLikePhone = (id) => /^\+?91\d{10}$/.test(id) || /^\d{10}$/.test(id);

// ── Modal ──────────────────────────────────────────────────────────────────────
function DataModal({ modal, onClose }) {
    if (!modal) return null;
    return (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
            <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "18px", width: "100%", maxWidth: "860px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
                <div style={{ background: modal.color, padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <h5 className="text-white fw-bold mb-0">{modal.icon} {modal.title}</h5>
                        <small style={{ color: "rgba(255,255,255,0.8)" }}>{modal.label} · {modal.data.length} records</small>
                    </div>
                    <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "50%", width: 32, height: 32, color: "#fff", fontSize: 16, cursor: "pointer" }}>✕</button>
                </div>
                <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                    {modal.data.length === 0 ? (
                        <div className="text-center text-muted py-5">No records found</div>
                    ) : (
                        <table className="table table-sm table-hover mb-0" style={{ fontSize: "13px" }}>
                            <thead style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 1 }}>
                                <tr>
                                    <th style={{ padding: "10px 16px", width: 40 }}>#</th>
                                    {modal.fields.map(f => <th key={f.key} style={{ padding: "10px 16px" }}>{f.label}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {modal.data.map((row, i) => (
                                    <tr key={i}>
                                        <td style={{ padding: "8px 16px", color: "#94a3b8" }}>{i + 1}</td>
                                        {modal.fields.map(f => <td key={f.key} style={{ padding: "8px 16px" }}>{fmtVal(row[f.key])}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function DataMetrixByProfile() {
    const [matrix, setMatrix]             = useState(null);
    const [profileTypes, setProfileTypes] = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error,   setError]             = useState(null);
    const [modal,   setModal]             = useState(null);
    const [selMonths, setSelMonths]       = useState(MONTHS.map((_, i) => i)); // all selected by default

    // Cached raw snapshots + derived maps — persists across month filter changes
    const snapsRef = useRef(null);
    const mapsRef  = useRef(null);

    useEffect(() => { fetchAll(); }, []);

    // Recompute whenever month selection changes (no re-fetch needed)
    useEffect(() => {
        if (snapsRef.current && mapsRef.current) {
            computeAll(snapsRef.current, mapsRef.current, selMonths);
        }
    }, [selMonths]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleMonth = (idx) => {
        setSelMonths(prev =>
            prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx].sort((a, b) => a - b)
        );
    };

    const selectAll = () => setSelMonths(MONTHS.map((_, i) => i));

    const openModal = (title, icon, color, data, fields, label) => {
        setModal({ title, icon, color, data, fields, label });
    };

    // ── Fetch: load raw snaps once ────────────────────────────────────────────
    const fetchAll = async () => {
        setLoading(true);
        setError(null);
        try {
            const [
                profileTypeSnap, downloadSnap, activitySnap,
                regSnap, portfolioSnap, collateralSnap, parlourSnap, workshopSnap,
            ] = await Promise.all([
                getDocs(collection(db, "profile_type")),
                getDocs(collection(db, "personal_details")),
                getDocs(collection(db, "users_activity")),
                getDocs(collection(db, "workshop_registration")),
                getDocs(collection(db, "nv_portfolio_details")),
                getDocs(collection(db, "nv_orders_data")),
                getDocs(collection(db, "parlour_name")),
                getDocs(collection(db, "workshop_event_data")),
            ]);

            // Build phone → profile_type map (all time, no date filter)
            const phoneToType = {};
            profileTypeSnap.docs.forEach(d => {
                const { phone_no, profile_type } = d.data();
                if (phone_no && profile_type) phoneToType[String(phone_no).trim()] = profile_type.trim();
            });

            // Sorted profile types by member count desc
            const typeCounts = {};
            Object.values(phoneToType).forEach(t => { typeCounts[t] = (typeCounts[t] || 0) + 1; });
            const allTypes = Object.keys(typeCounts).sort((a, b) => typeCounts[b] - typeCounts[a]);
            setProfileTypes(allTypes);

            // Cities/states that existed before Jan 2026 (never changes with month filter)
            const beforeQ1Par = parlourSnap.docs.filter(d => {
                const dt = toDate(d.data().created_date);
                return dt && dt < PERIOD_START;
            });
            const preExistingCityKeys  = new Set(beforeQ1Par.map(d => (d.data().city  || "").trim().toLowerCase()).filter(s => s));
            const preExistingStateKeys = new Set(beforeQ1Par.map(d => (d.data().state || "").trim().toLowerCase()).filter(s => s));

            const snaps = { downloadSnap, activitySnap, regSnap, portfolioSnap, collateralSnap, parlourSnap, workshopSnap };
            const maps  = { phoneToType, allTypes, preExistingCityKeys, preExistingStateKeys };

            snapsRef.current = snaps;
            mapsRef.current  = maps;

            computeAll(snaps, maps, selMonths);
        } catch (err) {
            console.error(err);
            setError("Failed to load data. Check console for details.");
            setLoading(false);
        }
    };

    // ── Compute: filter by selected months and aggregate ─────────────────────
    const computeAll = (snaps, maps, selectedIdxs) => {
        const { downloadSnap, activitySnap, regSnap, portfolioSnap, collateralSnap, parlourSnap, workshopSnap } = snaps;
        const { phoneToType, allTypes, preExistingCityKeys, preExistingStateKeys } = maps;

        const inSel = (val) => inMonths(val, selectedIdxs);
        const getType = (phone) => phoneToType[String(phone || "").trim()] || "Others";

        const groupDocs = (docs, getPhone) => {
            const g = {};
            allTypes.forEach(t => { g[t] = []; });
            if (!g["Others"]) g["Others"] = [];
            docs.forEach(d => {
                const t = getType(getPhone(d.data()));
                if (!g[t]) g[t] = [];
                g[t].push(d.data());
            });
            return g;
        };

        // Date-filtered docs using selected months
        const dlDocs  = downloadSnap.docs.filter(d => inSel(d.data().created_date));
        const auDocs  = activitySnap.docs.filter(d => inSel(d.data().last_active));
        const ptDocs  = regSnap.docs.filter(d => inSel(d.data().workshop_Date));
        const pfDocs  = portfolioSnap.docs.filter(d => inSel(d.data().created_date));
        const mcDocs  = collateralSnap.docs.filter(d => inSel(d.data().order_date));
        const parDocs = parlourSnap.docs.filter(d => inSel(d.data().created_date));
        const wsDocs  = workshopSnap.docs.filter(d => inSel(d.data().created_date));

        // Group by profile type
        const dlGroups  = groupDocs(dlDocs,  r => r.phone_no);
        const ptGroups  = groupDocs(ptDocs,  r => r.phone);
        const pfGroups  = groupDocs(pfDocs,  r => r.phone_no);
        const mcGroups  = groupDocs(mcDocs,  r => r.phone_no);
        const parGroups = groupDocs(parDocs, r => r.phone_no);

        // Active users — normalize doc ID (phone / +91-phone / random UID)
        const auGroups = {};
        allTypes.forEach(t => { auGroups[t] = []; });
        if (!auGroups["Others"]) auGroups["Others"] = [];
        auDocs.forEach(d => {
            let t;
            if (looksLikePhone(d.id)) {
                t = phoneToType[normalizeActivityId(d.id)] || "Others";
            } else {
                t = "Others";
            }
            if (!auGroups[t]) auGroups[t] = [];
            auGroups[t].push({ _id: d.id, ...d.data() });
        });

        // Workshops grouped by tag → profile type
        const wsGroups = {};
        allTypes.forEach(t => { wsGroups[t] = []; });
        if (!wsGroups["Others"]) wsGroups["Others"] = [];
        wsDocs.forEach(d => {
            const t = getProfileForTag(d.data().tag);
            if (!wsGroups[t]) wsGroups[t] = [];
            wsGroups[t].push(d.data());
        });

        // Build per-type metric objects
        const result = {};
        allTypes.forEach(type => {
            const wsForType = wsGroups[type] || [];
            const wsHours   = parseFloat((wsForType.length * 1.5).toFixed(1));

            const phoneCnt = {};
            (ptGroups[type] || []).forEach(r => { if (r.phone) phoneCnt[r.phone] = (phoneCnt[r.phone] || 0) + 1; });
            const repData = Object.entries(phoneCnt)
                .filter(([, c]) => c > 1)
                .map(([phone, count]) => ({ phone, registrations: count }));

            const cityMap = {};
            (parGroups[type] || []).forEach(r => {
                const c = (r.city || "").trim();
                if (!c) return;
                const k = c.toLowerCase();
                if (preExistingCityKeys.has(k)) return;
                if (!cityMap[k]) cityMap[k] = { city: c, state: r.state || "", parlour_count: 0 };
                cityMap[k].parlour_count++;
            });
            const cityData = Object.values(cityMap).sort((a, b) => a.city.localeCompare(b.city));

            const stateMap = {};
            (parGroups[type] || []).forEach(r => {
                const s = (r.state || "").trim();
                const c = (r.city  || "").trim();
                if (!s) return;
                const k = s.toLowerCase();
                if (preExistingStateKeys.has(k)) return;
                if (!stateMap[k]) stateMap[k] = { state: s, cities: new Set(), parlour_count: 0 };
                if (c) stateMap[k].cities.add(c.toLowerCase());
                stateMap[k].parlour_count++;
            });
            const stateData = Object.values(stateMap)
                .map(({ cities, ...rest }) => ({ ...rest, city_count: cities.size }))
                .sort((a, b) => a.state.localeCompare(b.state));

            result[type] = {
                downloads:    { count: dlGroups[type].length,  data: dlGroups[type],  fields: [{ key: "phone_no", label: "Phone" }, { key: "name", label: "Name" }, { key: "created_date", label: "Date" }] },
                active:       { count: auGroups[type].length,  data: auGroups[type],  fields: [{ key: "_id", label: "Phone" }, { key: "last_active", label: "Last Active" }] },
                workshops:    { count: wsForType.length,        data: wsForType,        fields: [{ key: "title", label: "Workshop" }, { key: "tag", label: "Tag" }, { key: "created_date", label: "Date" }] },
                hours:        { count: wsHours,                 data: wsForType.map(w => ({ ...w, hours: 1.5 })), fields: [{ key: "title", label: "Workshop" }, { key: "tag", label: "Tag" }, { key: "hours", label: "Hours" }] },
                participants: { count: ptGroups[type].length,  data: ptGroups[type],  fields: [{ key: "phone", label: "Phone" }, { key: "name", label: "Name" }, { key: "workshop_Date", label: "Date" }, { key: "workshop_name", label: "Workshop" }] },
                repeat:       { count: repData.length,          data: repData,          fields: [{ key: "phone", label: "Phone" }, { key: "registrations", label: "Times" }] },
                portfolios:   { count: pfGroups[type].length,  data: pfGroups[type],  fields: [{ key: "phone_no", label: "Phone" }, { key: "name", label: "Name" }, { key: "created_date", label: "Date" }] },
                collaterals:  { count: mcGroups[type].length,  data: mcGroups[type],  fields: [{ key: "phone_no", label: "Phone" }, { key: "order_type", label: "Type" }, { key: "order_date", label: "Date" }] },
                cities:       { count: cityData.length,         data: cityData,         fields: [{ key: "city", label: "City" }, { key: "state", label: "State" }, { key: "parlour_count", label: "Parlours" }] },
                states:       { count: stateData.length,        data: stateData,        fields: [{ key: "state", label: "State" }, { key: "city_count", label: "Unique Cities" }, { key: "parlour_count", label: "Parlours" }] },
            };
        });

        setMatrix(result);
        setLoading(false);
    };

    const modalLabel = selMonths.length === MONTHS.length
        ? `Jan–${MONTHS[MONTHS.length - 1].label} (All)`
        : selMonths.map(i => MONTHS[i].label).join(", ");

    return (
        <>
            <DataModal modal={modal} onClose={() => setModal(null)} />

            {/* HEADER */}
            <div style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)", padding: "20px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    <h4 className="text-white fw-bold mb-0">📊 Metrics by Profile Type</h4>
                    <small style={{ color: "#a0aec0" }}>{MONTHS.map(m => m.label).join(" · ")} · click any cell to inspect</small>
                </div>
                <div className="d-flex gap-3 align-items-center">
                    <Link to="/data_metrix" className="btn btn-sm btn-outline-light rounded-pill px-3">← Overall</Link>
                    <button className="btn btn-sm btn-outline-light rounded-pill px-3" onClick={fetchAll} disabled={loading}>
                        {loading ? "⏳ Loading…" : "🔄 Refresh"}
                    </button>
                    <Link to="/"><img src="/images/menubutton.png" alt="Menu" style={{ height: "2rem", filter: "invert(1)" }} /></Link>
                </div>
            </div>

            <div style={{ background: "#f0f4f8", minHeight: "100vh", padding: "28px 20px" }}>
                {error && <div className="alert alert-danger rounded-3">{error}</div>}

                {/* MONTH FILTER */}
                {!loading && (
                    <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>Filter by month:</span>
                        {MONTHS.map((m, i) => {
                            const active = selMonths.includes(i);
                            return (
                                <button
                                    key={i}
                                    onClick={() => toggleMonth(i)}
                                    style={{
                                        padding: "6px 16px",
                                        borderRadius: "20px",
                                        border: "2px solid #4361ee",
                                        background: active ? "#4361ee" : "#fff",
                                        color: active ? "#fff" : "#4361ee",
                                        fontWeight: 700,
                                        fontSize: "13px",
                                        cursor: "pointer",
                                        transition: "all 0.15s",
                                    }}
                                >
                                    {m.label}
                                </button>
                            );
                        })}
                        <button
                            onClick={selectAll}
                            style={{
                                padding: "6px 16px",
                                borderRadius: "20px",
                                border: "2px solid #94a3b8",
                                background: selMonths.length === MONTHS.length ? "#94a3b8" : "#fff",
                                color: selMonths.length === MONTHS.length ? "#fff" : "#94a3b8",
                                fontWeight: 700,
                                fontSize: "13px",
                                cursor: "pointer",
                            }}
                        >
                            All
                        </button>
                        <span style={{ fontSize: "12px", color: "#94a3b8", marginLeft: "4px" }}>
                            Showing: {modalLabel}
                        </span>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
                        <div className="mt-3 text-muted fw-semibold">Loading profile metrics…</div>
                    </div>
                ) : matrix && selMonths.length === 0 ? (
                    <div className="alert alert-warning rounded-3">Select at least one month to view data.</div>
                ) : matrix && (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", minWidth: "980px", borderCollapse: "separate", borderSpacing: 0, borderRadius: "16px", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.1)" }}>
                            <thead>
                                <tr style={{ background: "#1a1a2e" }}>
                                    <th style={{ padding: "16px 20px", color: "#a0aec0", fontWeight: 700, fontSize: "13px", borderBottom: "1px solid rgba(255,255,255,0.08)", minWidth: "140px" }}>
                                        Profile Type
                                    </th>
                                    {METRIC_DEFS.map(m => (
                                        <th key={m.key} style={{ padding: "12px 10px", textAlign: "center", borderBottom: "1px solid rgba(255,255,255,0.08)", borderLeft: "1px solid rgba(255,255,255,0.05)", minWidth: "80px" }}>
                                            <div style={{ fontSize: "16px" }}>{m.icon}</div>
                                            <div style={{ color: m.color, fontSize: "11px", fontWeight: 700, marginTop: "3px", lineHeight: 1.3 }}>{m.label}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody style={{ background: "#fff" }}>
                                {profileTypes.map((type, ti) => {
                                    const row = matrix[type];
                                    if (!row) return null;
                                    const pColor = PROFILE_COLORS[ti % PROFILE_COLORS.length];
                                    return (
                                        <tr key={type}>
                                            <td style={{ padding: "14px 20px", borderBottom: "1px solid #f0f4f8" }}>
                                                <span style={{ background: pColor, color: "#fff", padding: "5px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" }}>
                                                    {type}
                                                </span>
                                            </td>
                                            {METRIC_DEFS.map(m => {
                                                const cell   = row[m.key];
                                                const val    = cell?.count ?? "—";
                                                const suffix = m.suffix || "";
                                                return (
                                                    <td
                                                        key={m.key}
                                                        style={{ padding: "14px 10px", textAlign: "center", cursor: "pointer", borderBottom: "1px solid #f0f4f8", borderLeft: "1px solid #f0f4f8", transition: "background 0.15s" }}
                                                        onClick={() => openModal(`${type} — ${m.label}`, m.icon, m.color, cell?.data || [], cell?.fields || [], modalLabel)}
                                                        onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                                                        onMouseLeave={e => e.currentTarget.style.background = ""}
                                                        title="Click to inspect"
                                                    >
                                                        <div style={{ fontSize: "1.25rem", fontWeight: 800, color: m.color, lineHeight: 1 }}>
                                                            {val}{suffix}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}

                                {/* TOTAL ROW */}
                                <tr style={{ background: "#f8fafc" }}>
                                    <td style={{ padding: "14px 20px", fontWeight: 800, color: "#1a1a2e", fontSize: "13px", letterSpacing: "0.05em" }}>
                                        TOTAL
                                    </td>
                                    {METRIC_DEFS.map(m => {
                                        const total = profileTypes.reduce((sum, t) => {
                                            const v = matrix[t]?.[m.key]?.count ?? 0;
                                            return sum + (typeof v === "number" ? v : 0);
                                        }, 0);
                                        const suffix  = m.suffix || "";
                                        const display = m.key === "hours" ? parseFloat(total.toFixed(1)) : total;
                                        return (
                                            <td key={m.key} style={{ padding: "14px 10px", textAlign: "center", borderLeft: "1px solid #e2e8f0" }}>
                                                <div style={{ fontSize: "1rem", fontWeight: 800, color: "#475569" }}>
                                                    {display}{suffix}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}
